import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { useAlerts } from '@/hooks/useAlerts'
import { Plus, Search, Edit } from 'lucide-react'
import { ROUTES } from '@/config/routes'

export const AlertAllListPage = () => {
  const { data, isLoading, error } = useAlerts()
  const [searchTerm, setSearchTerm] = useState('')
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  if (error) {
    return <ErrorMessage message="Failed to load alerts. Please try again." />
  }

  const filteredAlerts = data?.alerts.filter(alert => {
    const matchesSearch = alert.alertName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.alertMessage.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.measurementColumn.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesSeverity = severityFilter === 'all' || alert.severityLevel === severityFilter
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && alert.isActive) ||
      (statusFilter === 'inactive' && !alert.isActive)
    return matchesSearch && matchesSeverity && matchesStatus
  }) || []

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300'
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300'
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-300'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">All Alert Configurations</h1>
          <p className="text-muted-foreground">
            Complete list of all system alert configurations
          </p>
        </div>
        <Button asChild>
          <Link to={ROUTES.ALERT_NEW}>
            <Plus className="mr-2 h-4 w-4" />
            New Alert
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search alerts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="severity">Severity</Label>
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger id="severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {filteredAlerts.length} of {data?.alerts.length || 0} alerts
            </p>
          </div>

          <div className="grid gap-4">
            {filteredAlerts.map((alert) => (
              <Card key={alert.id} className="hover:bg-muted/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Link
                          to={`/alerts/${alert.id}`}
                          className="text-lg font-semibold hover:underline"
                        >
                          {alert.alertName}
                        </Link>
                        <Badge className={getSeverityColor(alert.severityLevel)}>
                          {alert.severityLevel.toUpperCase()}
                        </Badge>
                        <Badge variant={alert.isActive ? 'success' : 'secondary'}>
                          {alert.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {alert.alertMessage}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Measurement: </span>
                          <span className="font-medium">
                            {alert.paramLabel || alert.measurementColumn}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Condition: </span>
                          <span className="font-medium">
                            {alert.comparisonOperator} {alert.thresholdValue} {alert.unit || ''}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Devices: </span>
                          <span className="font-medium">{alert.deviceCount}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Recipients: </span>
                          <span className="font-medium">{alert.recipientCount}</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" asChild>
                      <Link to={`/alerts/${alert.id}/edit`}>
                        <Edit className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredAlerts.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No alerts found matching your criteria
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
